import axios, { AxiosRequestConfig, AxiosResponse } from "axios"

/**
 * Axios initializing
 */

const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 30000
})

/**
 * Interceptors
 */

// Add a request interceptor
axiosInstance.interceptors.request.use(
  function (config) {
    // Do something before request is sent

    return config
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error)
  }
)

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data

    return response
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error

    return Promise.reject(error)
  }
)

/**
 * Send request
 */

const send = async <T = any>({
  method,
  url,
  data,
  responseType = "json",
  headers = {},
  params
}: AxiosRequestConfig): Promise<{ data: T; status: number }> => {
  const config = {
    method,
    url,
    data,
    params,
    headers,
    responseType
  }

  try {
    const response: AxiosResponse<T> = await axiosInstance.request<T>(config)
    return { data: response.data, status: response.status }
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * ApiService
 */

type ApiResponse<T = any> = Promise<{ data: T; status: number }>

const ApiService = {
  get: <T = any>(
    url: string,
    params?: Record<string, any>,
    headers?: Record<string, string>
  ): ApiResponse<T> => {
    return send<T>({
      method: "GET",
      url,
      params,
      headers
    })
  },

  delete: <T = any>(url: string): ApiResponse<T> => {
    return send<T>({
      method: "DELETE",
      url
    })
  },

  post: <T = any>(
    url: string,
    data?: any,
    headers?: Record<string, string>
  ): ApiResponse<T> => {
    return send<T>({
      method: "POST",
      url,
      data,
      headers
    })
  },

  put: <T = any>(url: string, data?: any): ApiResponse<T> => {
    return send<T>({
      method: "PUT",
      url,
      data
    })
  },

  patch: <T = any>(url: string, data?: any): ApiResponse<T> => {
    return send<T>({
      method: "PATCH",
      url,
      data
    })
  },

  getBlob: (url: string): ApiResponse<Blob> => {
    return send<Blob>({
      method: "GET",
      url,
      responseType: "blob"
    })
  },

  postFormData: <T = any>(url: string, data: FormData): ApiResponse<T> => {
    return send<T>({
      method: "POST",
      url,
      data,
      headers: { "Content-Type": "multipart/form-data" }
    })
  }
}

export default ApiService
